export type PostSource = {
  label: string;
  url: string;
};

export type ArticleVisualType = "metrics" | "flow" | "signals";

export type ArticleVisualItem = {
  value: string;
  label: string;
  note: string;
};

export type ArticleVisual = {
  key: string;
  type: ArticleVisualType;
  eyebrow: string;
  title: string;
  description: string;
  items: ArticleVisualItem[];
  sourceUrls: string[];
};

export type ArticleHeroImage = {
  src: string;
  alt: string;
};

export type BlogPost = {
  slug: string;
  title: string;
  dek: string;
  excerpt: string;
  category: string;
  readingMinutes: number;
  publishedAt: string;
  bodyMarkdown: string;
  sources: PostSource[];
  heroImage: ArticleHeroImage;
  visuals: ArticleVisual[];
};

export type BlogPostSummary = Omit<BlogPost, "bodyMarkdown" | "visuals"> & {
  bodyMarkdown?: string;
  visuals?: ArticleVisual[];
};

const EU_CATEGORY_URL =
  "https://food.ec.europa.eu/food-safety/labelling-and-nutrition/natural-mineral-waters-and-spring-water_en";
const EU_REGISTER_URL =
  "https://food.ec.europa.eu/document/download/ec4fbcc0-7185-4dce-820a-27f7e2653dad_en?filename=labelling-nutrition_mineral-waters_list_eu-recognised.pdf";
const DEWA_REPORT_URL =
  "https://www.dewa.gov.ae/-/media/Files/Sustainability/Sustainability_Report_English_2025.ashx";
const WHO_DESALINATION_URL =
  "https://www.who.int/publications/i/item/WHO-HSE-WSH-11.03";
const DUBAI_MUNICIPALITY_URL = "https://www.dm.gov.ae/dubai-municipality-services/";
const UAE_FOOD_SAFETY_URL =
  "https://u.ae/en/information-and-services/health-and-fitness/food-safety-and-health-tips";
const UAE_FOOD_LAW_URL = "https://uaelegislation.gov.ae/en/legislations/1161";
const UAE_ZAD_URL =
  "https://u.ae/en/information-and-services/health-and-fitness/food-safety-and-health-tips/national-food-accreditation-and-registration-system";
const UAE_WATER_STANDARD_URL = "https://uaelegislation.gov.ae/en/legislations/1758";
const UAE_TARIFF_URL =
  "https://www.dubaicustoms.gov.ae/en/PoliciesAndNotices/Documents/IntegratedCustomsTariff.pdf";
const UAE_VAT_URL = "https://uaelegislation.gov.ae/en/legislations/1227";
const UAE_TRADE_URL =
  "https://wits.worldbank.org/trade/comtrade/en/country/ARE/year/2023/tradeflow/Imports/partner/ALL/product/220110";
const BORSEC_UAE_SHOP_URL = "https://www.borsecuae.com/shop";
const BORSEC_UAE_HOME_URL = "https://www.borsecuae.com/";

export const fallbackPosts: BlogPost[] = ([
  {
    slug: "natural-mineral-water-what-the-words-mean",
    title: "Natural mineral water: what the words actually mean",
    dek: "A regulated source category, not a synonym for expensive bottled water.",
    excerpt:
      "The useful distinctions are underground origin, stable composition and source recognition—not wellness language.",
    category: "Mineral water",
    readingMinutes: 4,
    publishedAt: "2026-07-26",
    bodyMarkdown: `# Natural mineral water: what the words actually mean

“Natural mineral water” is a defined category in Europe. It comes from an underground source, is recognised for its original purity, and has a mineral composition that remains stable within natural variation.

That does not make every natural mineral water taste the same. Dissolved bicarbonate, calcium, magnesium, sodium and other constituents shape its sensory character. Carbonation and serving temperature matter too.

## Where Borsec fits

The European Union’s June 2026 register lists recognised waters from Borsec, Făget BORSEC and F1C in Harghita, Romania. That recognition is useful provenance, but it does not replace UAE food registration, label assessment or import approval.

## What the term does not prove

The category is not a medical recommendation and does not prove that one water is “healthier” than safe municipal drinking water. For this project, the honest proposition is taste, source and format.

Before any sale, the exact UAE SKU should carry its approved label and current analysis. That label—not a generic brand claim—will be the reference for mineral values.`,
    sources: [
      {
        label: "European Commission: natural mineral and spring waters",
        url: EU_CATEGORY_URL
      },
      {
        label: "EU list of recognised natural mineral waters, June 2026",
        url: EU_REGISTER_URL
      }
    ],
    heroImage: {
      src: "/assets/images/mineral-water-glass.jpg",
      alt:
        "A clear glass of sparkling mineral water beside a cold mountain stream."
    },
    visuals: [
      {
        key: "category-test",
        type: "metrics",
        eyebrow: "The category test",
        title: "Three ideas sit behind the regulated name",
        description:
          "Natural mineral water is defined by where it comes from and how consistently the source behaves.",
        items: [
          {
            value: "01",
            label: "Underground origin",
            note: "The water comes from a protected underground source."
          },
          {
            value: "02",
            label: "Original purity",
            note: "Protection at source—not a wellness promise—is central to the category."
          },
          {
            value: "03",
            label: "Stable character",
            note: "Its mineral composition remains characteristic within natural variation."
          }
        ],
        sourceUrls: [EU_CATEGORY_URL]
      },
      {
        key: "recognition-route",
        type: "flow",
        eyebrow: "From source to shelf",
        title: "Recognition is evidence, not a UAE sales permit",
        description:
          "European source status and UAE product approval answer different questions.",
        items: [
          {
            value: "Source",
            label: "Characterise the water",
            note: "Document origin, protection and composition."
          },
          {
            value: "EU",
            label: "Recognise the source",
            note: "The register records recognised natural mineral waters."
          },
          {
            value: "UAE",
            label: "Register the exact SKU",
            note: "The bottle and label still need the applicable local review."
          }
        ],
        sourceUrls: [EU_CATEGORY_URL, EU_REGISTER_URL]
      },
      {
        key: "claim-boundary",
        type: "signals",
        eyebrow: "What the label can say",
        title: "Provenance is not a health claim",
        description:
          "The strongest proposition is transparent source information and sensory character.",
        items: [
          {
            value: "Yes",
            label: "Named-source provenance",
            note: "Origin and recognised category status can be documented."
          },
          {
            value: "Yes",
            label: "Mineral profile and taste",
            note: "The approved analysis can explain a water’s characteristic profile."
          },
          {
            value: "No",
            label: "Medical superiority",
            note: "Category status does not prove treatment or superior hydration."
          }
        ],
        sourceUrls: [EU_CATEGORY_URL, EU_REGISTER_URL]
      }
    ]
  },
  {
    slug: "how-drinking-water-reaches-dubai-homes",
    title: "How drinking water reaches Dubai homes",
    dek: "Desalination, treatment, transmission and storage—explained without scare tactics.",
    excerpt:
      "Dubai’s system turns seawater into potable water at extraordinary scale. Imported mineral water is a taste choice, not a safety correction.",
    category: "Dubai water",
    readingMinutes: 5,
    publishedAt: "2026-07-25",
    bodyMarkdown: `# How drinking water reaches Dubai homes

Dubai’s municipal drinking water begins primarily as seawater. Desalination removes salts, and the water is then stabilised, treated, transmitted and stored before it reaches the city’s network.

DEWA reported 495 million imperial gallons per day of installed desalination capacity in 2025 and 158.4 billion imperial gallons produced during the year. It also states that the potable water it produces, transmits and distributes complies with current World Health Organization drinking-water requirements.

## Safe water and personal taste can both be true

This project should never be built on fear. A person can recognise Dubai’s safe, tightly managed municipal supply and still prefer a named-source mineral water at the table.

The meaningful difference is provenance and sensory profile: one is engineered at city scale from seawater; the other carries the characteristic mineral composition of a named underground source.

Building tanks and plumbing can affect the water at the point of use, which is a separate building-maintenance question from DEWA’s production quality.`,
    sources: [
      {
        label: "DEWA Sustainability Report 2025",
        url: DEWA_REPORT_URL
      },
      {
        label: "WHO guidance on desalinated drinking water",
        url: WHO_DESALINATION_URL
      }
    ],
    heroImage: {
      src: "/assets/images/journal-dubai-water-system.jpg",
      alt:
        "Dubai desalination and water-storage infrastructure beside the Gulf and city skyline."
    },
    visuals: [
      {
        key: "system-scale",
        type: "metrics",
        eyebrow: "DEWA · 2025",
        title: "A city-scale water system",
        description:
          "The reported figures describe the scale of production—not a comparison with bottled water.",
        items: [
          {
            value: "495 MIGD",
            label: "Installed desalination capacity",
            note: "Million imperial gallons per day reported for 2025."
          },
          {
            value: "158.4bn IG",
            label: "Water produced",
            note: "Total imperial gallons reported during the year."
          },
          {
            value: "WHO",
            label: "Potable-water requirements",
            note: "DEWA reports compliance across production, transmission and distribution."
          }
        ],
        sourceUrls: [DEWA_REPORT_URL]
      },
      {
        key: "sea-to-home",
        type: "flow",
        eyebrow: "The managed journey",
        title: "From Gulf seawater to the urban network",
        description:
          "Desalination is one stage in a complete drinking-water system.",
        items: [
          {
            value: "01",
            label: "Desalinate",
            note: "Remove salts from seawater."
          },
          {
            value: "02",
            label: "Treat and stabilise",
            note: "Prepare the water for safe distribution."
          },
          {
            value: "03",
            label: "Transmit and store",
            note: "Move potable water through the city-scale network."
          },
          {
            value: "04",
            label: "Maintain the building",
            note: "Private tanks and plumbing remain a separate point-of-use responsibility."
          }
        ],
        sourceUrls: [DEWA_REPORT_URL, WHO_DESALINATION_URL]
      },
      {
        key: "two-questions",
        type: "signals",
        eyebrow: "A useful distinction",
        title: "Safety and preference are different questions",
        description:
          "Imported mineral water can be a provenance and taste choice without fear-based claims.",
        items: [
          {
            value: "System",
            label: "Dubai potable water",
            note: "Managed at urban scale against drinking-water requirements."
          },
          {
            value: "Source",
            label: "Natural mineral water",
            note: "Chosen for named origin, composition, carbonation or taste."
          },
          {
            value: "Choice",
            label: "Both can be valid",
            note: "Preference does not require calling the municipal system unsafe."
          }
        ],
        sourceUrls: [DEWA_REPORT_URL, WHO_DESALINATION_URL]
      }
    ]
  },
  {
    slug: "from-borsec-to-jebel-ali-import-path",
    title: "From Borsec to Jebel Ali: what importing would take",
    dek: "A market test is the beginning of diligence, not permission to sell.",
    excerpt:
      "Product registration, Arabic label review, conformity, freight, heat-safe storage and consignment release all come before a real order.",
    category: "Import diary",
    readingMinutes: 6,
    publishedAt: "2026-07-24",
    bodyMarkdown: `# From Borsec to Jebel Ali: what importing would take

An encouraging tasting is not an import licence. The commercial path starts with authorization and the exact product dossier, then moves through UAE conformity and Dubai food-registration requirements.

## Before a pallet moves

The precise still or sparkling SKU, source, composition, package material and label need to be fixed. Dubai Municipality provides services to register and assess a food item, review its label against applicable standards, and release imported food consignments for the local market.

Mandatory information needs compliant Arabic presentation. Product test reports, manufacturing controls and distributor evidence may be required under the relevant conformity route. These details should be confirmed with an authorised UAE food importer and the current authorities before taking payment.

## Economics are part of product truth

The landed price includes more than the bottle: inland transport, sea freight, insurance, duty, VAT, storage, breakage, last-mile delivery and channel margin. Imported water also carries a sustainability trade-off, particularly when heavy glass is moved long distances.

That is why this site asks about exact formats and a visible price. A waitlist without price acceptance would be a flattering but weak signal.`,
    sources: [
      {
        label: "Dubai Municipality food-product and import services",
        url: DUBAI_MUNICIPALITY_URL
      },
      {
        label: "UAE government food-safety guidance",
        url: UAE_FOOD_SAFETY_URL
      }
    ],
    heroImage: {
      src: "/assets/images/borsec-carpathians-dubai.jpg",
      alt:
        "A chilled mineral-water bottle linking a cool mountain source with warm Dubai desert."
    },
    visuals: [
      {
        key: "import-gates",
        type: "flow",
        eyebrow: "Before a pallet moves",
        title: "Five gates separate interest from a compliant shipment",
        description:
          "Each step attaches to the exact bottle, pack and importer—not simply to the brand name.",
        items: [
          {
            value: "01",
            label: "Authorisation",
            note: "Confirm a legitimate supply relationship and exact SKU."
          },
          {
            value: "02",
            label: "Technical file",
            note: "Fix composition, package, manufacturing and product evidence."
          },
          {
            value: "03",
            label: "Register and assess",
            note: "Submit the food item and its Arabic label through the applicable route."
          },
          {
            value: "04",
            label: "Move and store",
            note: "Protect the shipment from freight and heat risks."
          },
          {
            value: "05",
            label: "Release",
            note: "Complete consignment checks before local sale."
          }
        ],
        sourceUrls: [DUBAI_MUNICIPALITY_URL, UAE_FOOD_SAFETY_URL]
      },
      {
        key: "landed-cost",
        type: "metrics",
        eyebrow: "The real price stack",
        title: "The bottle is only one line in landed cost",
        description:
          "A viable delivered price must survive the full route to the customer.",
        items: [
          {
            value: "Freight",
            label: "Long-distance movement",
            note: "Inland haulage, sea freight, insurance and handling."
          },
          {
            value: "Border",
            label: "Import charges",
            note: "Duty, VAT, conformity and consignment costs as applicable."
          },
          {
            value: "Dubai",
            label: "Local fulfilment",
            note: "Storage, breakage, delivery and channel margin."
          }
        ],
        sourceUrls: [DUBAI_MUNICIPALITY_URL, UAE_FOOD_SAFETY_URL]
      },
      {
        key: "evidence-ladder",
        type: "signals",
        eyebrow: "Market-test discipline",
        title: "Interest becomes useful when commitment increases",
        description:
          "Each signal answers a harder commercial question than the one before it.",
        items: [
          {
            value: "Weak",
            label: "A like or email",
            note: "Useful for reach, but silent on price and repeat behaviour."
          },
          {
            value: "Better",
            label: "A format and price choice",
            note: "Shows a preference under more realistic conditions."
          },
          {
            value: "Strong",
            label: "A compliant pilot commitment",
            note: "Connects demand with timing, volume and landed economics."
          }
        ],
        sourceUrls: [DUBAI_MUNICIPALITY_URL, UAE_FOOD_SAFETY_URL]
      }
    ]
  },
  {
    slug: "uae-imported-mineral-water-market-case-study",
    title: "Imported mineral water in the UAE: a market-entry case study",
    dek:
      "A concentrated import category, a demanding compliance route and a small Romanian foothold.",
    excerpt:
      "Trade data, border taxes and a live Borsec storefront reveal the opportunity—and the evidence still missing.",
    category: "Market case study",
    readingMinutes: 8,
    publishedAt: "2026-07-26T07:00:00.000Z",
    bodyMarkdown: `# Imported mineral water in the UAE: a market-entry case study

The UAE is already a meaningful destination for imported mineral and aerated water. That does not automatically make a new Romanian import viable. The useful question is narrower: can a source-led product clear the regulatory route, reach customers in good condition and earn repeat demand at a landed price that works?

This case study combines official market-access requirements, a 2023 trade snapshot and a current online storefront. It is a picture of observable conditions, not a claim about sales, market share or future demand.

## What the trade data can—and cannot—tell us

The [WITS presentation of UN Comtrade data](https://wits.worldbank.org/trade/comtrade/en/country/ARE/year/2023/tradeflow/Imports/partner/ALL/product/220110) reports that the UAE imported US$86.726 million and 151.971 million litres under HS 220110 in 2023. Romania accounted for US$100,600 and 124,950 litres, or roughly 0.12% of reported import value.

That is a real category signal, but it needs a precise label. HS 220110 combines unsweetened mineral waters and aerated waters. It is not a Borsec sales figure, and the Romania line does not identify brands, channels or customers. The data shows that imported water is established while Romania’s reported position was small; it does not prove how large the addressable market is for a particular SKU in 2026.

The concentration is also instructive. France, Italy and Norway represented most reported import value, which suggests that established source countries and distribution systems already shape the category. A Romanian proposition therefore needs more than origin. It needs a clear format, a credible route to market and a reason for repeat purchase.

## The five market-access gates

The first gate is a properly authorised food business. [Federal Law No. 10 of 2015](https://uaelegislation.gov.ae/en/legislations/1161) requires competent-authority approval for food-related commercial activity and requires food products to be registered before trading. Brand permission, an eligible UAE importer and a technical file for the exact still or sparkling SKU are therefore prerequisites, not details to solve after demand appears.

The second gate is national food registration. The UAE government’s [National Food Accreditation and Registration System guidance](https://u.ae/en/information-and-services/health-and-fitness/food-safety-and-health-tips/national-food-accreditation-and-registration-system) says imported, locally produced and materially relabelled or reformulated food must be registered in ZAD before it is handled in UAE markets.

The third gate is the local Dubai process. [Dubai Municipality](https://www.dm.gov.ae/dubai-municipality-services/) lists services to register and assess a food item, assess its label against local and GCC specifications, and release imported food consignments for the local market. The approved information has to match the physical bottle, outer pack and supporting documents.

The fourth gate is water-specific conformity and truthful Arabic information. [Cabinet Resolution No. 22 of 2022](https://uaelegislation.gov.ae/en/legislations/1758) lists UAE.S GSO 987:2021 for bottled natural mineral water among applicable technical regulations. The precise current evidence required for a particular package should be confirmed before stock moves.

The fifth gate is consignment release and suitable distribution. Water is heavy, glass can break and Dubai heat punishes careless storage. A compliant dossier without temperature-aware freight, protected storage and dependable last-mile handling is not a complete product.

## The border-tax waterfall

The [UAE integrated customs tariff](https://www.dubaicustoms.gov.ae/en/PoliciesAndNotices/Documents/IntegratedCustomsTariff.pdf) shows 5% duty for natural mineral water in containers up to one litre and over one litre. Dubai Customs describes duty as assessed on the cost, insurance and freight value.

An illustrative AED100 CIF shipment value therefore adds AED5 of customs duty. With a 5% import VAT applied to an AED105 base in this simplified illustration, the VAT is AED5.25 and the border total becomes AED110.25. The [UAE VAT law](https://uaelegislation.gov.ae/en/legislations/1227) provides the legal framework; an eligible VAT-registered business may recover input VAT, while customs duty is ordinarily a landed cost.

This is not a delivered-price calculation. Registration, testing, port handling, storage, breakage, delivery, customer acquisition and channel margin still sit beyond the border.

## A current UAE storefront signal

As of 26 July 2026, the [borsecuae.com shop](https://www.borsecuae.com/shop) displayed three Borsec-branded packs: 12 × 330 ml sparkling at AED132, 12 × 330 ml still at AED132 and 6 × 750 ml sparkling at AED84. The site’s [order page](https://www.borsecuae.com/) also stated a three-box minimum.

Those displayed prices work out to about AED33.33 per litre for either 330 ml case and AED18.67 per litre for the 750 ml sparkling pack. They are useful evidence that a direct online proposition exists and that pack format materially changes the visible price per litre.

The storefront does not, by itself, establish stock, importer identity, product registration, sales volume, market share, exclusivity or regulatory status. It should be treated as a dated market observation—not as proof of the category’s commercial performance.

## What the opportunity actually is

The evidence supports a disciplined pilot hypothesis. Imported water already has a substantial UAE trade category. Romanian supply represented a small reported share in 2023. A current Borsec storefront suggests some active market presence, while its format and minimum-order structure leave room to test different customer jobs: a home tasting pack, regular case delivery, hospitality glass and diaspora-led discovery.

The remaining questions are behavioural and operational:

- Which still or sparkling format wins a blind or semi-blind tasting?
- How many customers accept a transparent delivered price after the full landed-cost stack?
- Does interest repeat after nostalgia and curiosity fade?
- Can an authorised importer maintain compliant documents, protected stock and reliable delivery?
- Can the economics support customer service and channel margin without unrealistic volume assumptions?

The next useful move is not a container order. It is a qualified demand test tied to exact formats and visible prices, followed by a small compliant pilot only if supply authority, registration and economics all align.

SOURCE / SAND is an independent market-validation project and is not affiliated with or authorised by Borsec or Romaqua.`,
    sources: [
      { label: "UAE Federal Food Safety Law", url: UAE_FOOD_LAW_URL },
      { label: "UAE national food registration system", url: UAE_ZAD_URL },
      { label: "Dubai Municipality food services", url: DUBAI_MUNICIPALITY_URL },
      { label: "UAE bottled natural mineral water regulation", url: UAE_WATER_STANDARD_URL },
      { label: "UAE integrated customs tariff", url: UAE_TARIFF_URL },
      { label: "UAE VAT law", url: UAE_VAT_URL },
      { label: "WITS / UN Comtrade: UAE HS 220110 imports, 2023", url: UAE_TRADE_URL },
      { label: "borsecuae.com shop snapshot, 26 July 2026", url: BORSEC_UAE_SHOP_URL },
      { label: "borsecuae.com order page snapshot, 26 July 2026", url: BORSEC_UAE_HOME_URL }
    ],
    heroImage: {
      src: "/assets/images/journal-uae-imported-mineral-water-market.jpg",
      alt:
        "Unbranded glass mineral-water bottles arriving in crates at a Dubai port."
    },
    visuals: [
      {
        key: "trade-snapshot",
        type: "metrics",
        eyebrow: "HS 220110 · 2023",
        title: "A large import category, a small Romanian line",
        description:
          "The category includes unsweetened mineral and aerated waters; it is not a Borsec-specific market size.",
        items: [
          {
            value: "US$86.726m",
            label: "Total UAE import value",
            note: "Reported under HS 220110 for 2023."
          },
          {
            value: "151.971m L",
            label: "Total reported volume",
            note: "All partner countries combined."
          },
          {
            value: "0.12%",
            label: "Romania’s value share",
            note: "US$100,600 and 124,950 litres in the reported data."
          }
        ],
        sourceUrls: [UAE_TRADE_URL]
      },
      {
        key: "border-waterfall",
        type: "flow",
        eyebrow: "Illustrative border math",
        title: "AED100 CIF becomes AED110.25 at the border",
        description:
          "A simplified illustration using the published 5% duty and 5% VAT rates; operating costs come later.",
        items: [
          {
            value: "AED100.00",
            label: "CIF value",
            note: "Cost, insurance and freight starting point."
          },
          {
            value: "+ AED5.00",
            label: "Customs duty",
            note: "5% of the illustrative CIF value."
          },
          {
            value: "+ AED5.25",
            label: "Import VAT",
            note: "5% of the simplified AED105 VAT base."
          },
          {
            value: "AED110.25",
            label: "Border total",
            note: "Before registration, handling, storage, delivery and margin."
          }
        ],
        sourceUrls: [UAE_TARIFF_URL, UAE_VAT_URL]
      },
      {
        key: "storefront-snapshot",
        type: "signals",
        eyebrow: "borsecuae.com · 26 Jul 2026",
        title: "Three visible packs, two very different litre prices",
        description:
          "Displayed listings are a dated storefront observation—not evidence of sales, share, exclusivity or regulatory status.",
        items: [
          {
            value: "AED132",
            label: "12 × 330 ml · still or sparkling",
            note: "3.96 litres per case · about AED33.33/L."
          },
          {
            value: "AED84",
            label: "6 × 750 ml · sparkling",
            note: "4.50 litres per case · about AED18.67/L."
          },
          {
            value: "3 boxes",
            label: "Displayed minimum order",
            note: "The stated minimum; delivery terms require separate confirmation."
          }
        ],
        sourceUrls: [BORSEC_UAE_SHOP_URL, BORSEC_UAE_HOME_URL]
      }
    ]
  }
] satisfies BlogPost[]).sort((left, right) =>
  right.publishedAt.localeCompare(left.publishedAt)
);

export function fallbackPostsForLocale(locale: "en" | "ar" | "ru" | "ro") {
  return locale === "en" ? fallbackPosts : [];
}

export const faqItems = [
  {
    question: "What happens if enough people are interested?",
    answer:
      "We’ll use the signal to decide whether to pursue tastings, supplier discussions, import diligence and a compliant pilot. No order or payment is being taken."
  },
  {
    question: "Are you affiliated with Borsec?",
    answer:
      "No. SOURCE / SAND is an independent market-validation project and has no distribution agreement today."
  },
  {
    question: "Are you taking orders?",
    answer:
      "Not yet. We’re collecting qualified interest and tasting requests before any commercial step."
  },
  {
    question: "Is Dubai’s water unsafe?",
    answer:
      "No. DEWA reports that its potable water meets current WHO drinking-water requirements. This project is about taste and source provenance."
  }
];

export const mineralProfile = [
  { symbol: "HCO₃", label: "Bicarbonate", value: 1696, unit: "mg/L" },
  { symbol: "Ca", label: "Calcium", value: 345, unit: "mg/L" },
  { symbol: "Mg", label: "Magnesium", value: 109, unit: "mg/L" },
  { symbol: "Na", label: "Sodium", value: 75.2, unit: "mg/L" }
] as const;
